import { dom } from './dom.js';
import * as state from './state.js';
import { initializeCalendars } from './calendar.js';
import { loadTransactions } from './transactions.js';
import { Preferences } from '@capacitor/preferences';

let translations = {};
let currentLang = 'zh-TW'; // Default

export function t(key, variables) {
    let translation = translations[key] || key;
    if (variables) {
        Object.entries(variables).forEach(([varKey, varValue]) => {
            translation = translation.replace(`{${varKey}}`, varValue);
        });
    }
    return translation;
}

function updateStaticDOM() {
    document.querySelectorAll('[data-i18n]').forEach(el => {
        const key = el.dataset.i18n;
        el.textContent = t(key);
    });
    document.querySelectorAll('[data-i18n-placeholder]').forEach(el => {
        const key = el.dataset.i18nPlaceholder;
        el.placeholder = t(key);
    });
    if (dom.emptyState) {
        dom.emptyState.innerHTML = `
            <span data-i18n="emptyState">${t('emptyState')}</span>
            <span class="text-sm mt-2" data-i18n="emptyStateSub">${t('emptyStateSub')}</span>
        `;
    }
    if (dom.txDescription) {
        const currentType = dom.txType.value || 'expense';
        dom.txDescription.placeholder = t(currentType === 'income' ? 'itemPlaceholderIncome' : 'itemPlaceholderExpense');
    }
}

async function loadTranslations(lang) {
    try {
        const response = await fetch(`/lang/${lang}.json`);
        if (!response.ok) throw new Error(`Failed to load ${lang}.json`);
        translations = await response.json();
    } catch (error) {
        console.error("Error loading i18n file:", error);
        if (lang !== 'en') {
             try {
                const response = await fetch(`/lang/en.json`);
                translations = await response.json();
                currentLang = 'en';
             } catch (e) {
                translations = {};
             }
        }
    }
}

export async function setLanguage(lang) {
    // 允許重新設定相同語言，以便更新 UI 狀態
    if (!['en', 'zh-CN', 'zh-TW', 'ja', 'ko', 'de', 'es', 'fr', 'pt'].includes(lang)) {
        return;
    }
    
    const prevLang = currentLang;
    currentLang = lang;
    await Preferences.set({ key: 'ledger_lang', value: lang });
    document.documentElement.lang = lang;
    
    if (prevLang !== lang) {
        await loadTranslations(lang);
        updateStaticDOM();
        
        // 重置日曆
        if (state.monthSelectInstance) {
            state.monthSelectInstance.destroy();
            state.setMonthSelectInstance(null);
        }
        initializeCalendars(); 
        
        // 重新載入交易清單以更新類別名稱等動態文字
        await loadTransactions();
        
        // 發送語言變更事件，讓其他模組（如交易模組）自行響應，避免迴圈引用
        window.dispatchEvent(new CustomEvent('app:languageChanged', { detail: { lang } }));
    }

    updateLanguageViewUI();
}

async function getInitialLang() {
    const { value: savedLang } = await Preferences.get({ key: 'ledger_lang' });
    if (savedLang && ['en', 'zh-CN', 'zh-TW', 'ja', 'ko', 'de', 'es', 'fr', 'pt'].includes(savedLang)) {
        return savedLang;
    }
    const browserLang = navigator.language;
    
    if (browserLang.startsWith('zh-CN')) return 'zh-CN';
    if (browserLang.startsWith('zh')) return 'zh-TW';
    if (browserLang.startsWith('ja')) return 'ja';
    if (browserLang.startsWith('ko')) return 'ko';
    if (browserLang.startsWith('de')) return 'de';
    if (browserLang.startsWith('es')) return 'es';
    if (browserLang.startsWith('fr')) return 'fr';
    if (browserLang.startsWith('pt')) return 'pt';
    if (browserLang.startsWith('en')) return 'en';
    
    return 'en'; // Default
}

function bindLanguageViewEvents() {
    // 開啟語言設定視圖 (在 auth.js 中 switchView 已經處理了顯示邏輯，這裡主要處理返回)
    dom.openLangModalBtn.addEventListener('click', (e) => {
        e.preventDefault();
        
        // 隱藏其他 (增加存在性檢查，避免崩潰)
        if (dom.ledgerView) dom.ledgerView.style.display = 'none';
        if (dom.userSettingsView) dom.userSettingsView.style.display = 'none';
        if (dom.reportsView) dom.reportsView.style.display = 'none';
        
        // 顯示語言
        if (dom.languageSettingsView) dom.languageSettingsView.style.display = 'flex';
        updateLanguageViewUI();
    });

    dom.backFromLanguageBtn.addEventListener('click', (e) => {
        e.preventDefault();
        // 返回設置頁
        dom.languageSettingsView.style.display = 'none';
        dom.userSettingsView.style.display = 'flex';
    });

    // --- 隱私權政策視圖事件 ---
    if (dom.openPrivacyBtn) {
        dom.openPrivacyBtn.addEventListener('click', (e) => {
            e.preventDefault();
            // 隱藏其他視圖
            if (dom.ledgerView) dom.ledgerView.style.display = 'none';
            if (dom.userSettingsView) dom.userSettingsView.style.display = 'none';
            if (dom.languageSettingsView) dom.languageSettingsView.style.display = 'none';
            
            // 顯示隱私
            if (dom.privacyView) dom.privacyView.style.display = 'flex';
        });
    }

    if (dom.backFromPrivacyBtn) {
        dom.backFromPrivacyBtn.addEventListener('click', (e) => {
            e.preventDefault();
            // 返回設置頁
            if (dom.privacyView) dom.privacyView.style.display = 'none';
            if (dom.userSettingsView) dom.userSettingsView.style.display = 'flex';
        });
    }

    // 選擇語言後，自動返回上一頁
    const handleLangSelect = async (lang) => {
        await setLanguage(lang);
        // 返回設置頁
        dom.languageSettingsView.style.display = 'none';
        dom.userSettingsView.style.display = 'flex';
    };

    dom.langBtnEn.addEventListener('click', () => handleLangSelect('en'));
    dom.langBtnZhCN.addEventListener('click', () => handleLangSelect('zh-CN'));
    dom.langBtnZhTW.addEventListener('click', () => handleLangSelect('zh-TW'));
    
    // 增加存在性檢查，防止在 PC/Web 或部分語言未定義時報錯
    if (dom.langBtnJa) dom.langBtnJa.addEventListener('click', () => handleLangSelect('ja'));
    if (dom.langBtnKo) dom.langBtnKo.addEventListener('click', () => handleLangSelect('ko'));
    if (dom.langBtnDe) dom.langBtnDe.addEventListener('click', () => handleLangSelect('de'));
    if (dom.langBtnEs) dom.langBtnEs.addEventListener('click', () => handleLangSelect('es'));
    if (dom.langBtnFr) dom.langBtnFr.addEventListener('click', () => handleLangSelect('fr'));
    if (dom.langBtnPt) dom.langBtnPt.addEventListener('click', () => handleLangSelect('pt'));
}

function updateLanguageViewUI() {
    const buttons = [
        dom.langBtnEn, dom.langBtnZhCN, dom.langBtnZhTW, 
        dom.langBtnJa, dom.langBtnKo, dom.langBtnDe, 
        dom.langBtnEs, dom.langBtnFr, dom.langBtnPt
    ].filter(btn => btn !== null && btn !== undefined); // 過濾掉不存在的按鈕

    const langMap = { 
        'en': dom.langBtnEn, 'zh-CN': dom.langBtnZhCN, 'zh-TW': dom.langBtnZhTW,
        'ja': dom.langBtnJa, 'ko': dom.langBtnKo, 'de': dom.langBtnDe,
        'es': dom.langBtnEs, 'fr': dom.langBtnFr, 'pt': dom.langBtnPt
    };
    
    // 重置所有按鈕樣式
    buttons.forEach(btn => {
        if (!btn) return;
        const checkIcon = btn.querySelector('.check-icon');
        const textSpan = btn.querySelector('span');
        
        // 移除激活狀態 (字體恢復正常)
        textSpan.classList.remove('font-bold', 'text-indigo-600', 'dark:text-indigo-400');
        textSpan.classList.add('text-gray-900', 'dark:text-white');
        
        // 隱藏勾選圖示
        checkIcon.classList.add('hidden');
    });
    
    // 激活當前語言
    const activeBtn = langMap[currentLang];
    if (activeBtn) {
        const checkIcon = activeBtn.querySelector('.check-icon');
        const textSpan = activeBtn.querySelector('span');

        // 套用激活樣式 (字體變色/加粗)
        textSpan.classList.remove('text-gray-900', 'dark:text-white');
        textSpan.classList.add('font-bold', 'text-indigo-600', 'dark:text-indigo-400');
        
        // 顯示勾選圖示
        checkIcon.classList.remove('hidden');
    }
}

export async function initializeI18n() {
    currentLang = await getInitialLang();
    document.documentElement.lang = currentLang;
    
    await loadTranslations(currentLang);
    bindLanguageViewEvents();
    updateStaticDOM();
}

export { currentLang };